import { Button, ButtonVariant } from '@sas/nova-commons/components/Button';
import { ToggleButton } from '@sas/nova-commons/components/ToggleButton';
import { setRef } from '@sas/nova-commons/util/ref-util';
import { PinPropertyPane, Search, UnpinnedPropertyPane } from '@sas/react-svg';
import React, { useCallback, useEffect, useRef, useState, } from 'react';
import { connect } from 'react-redux';
import { PinPanel, SearchResultPanel, MainSearchPanel, GeographicAreaPanel, RoutePanel, } from './panels';
import { Panel } from './Panel';
import { isGeographicRoute, isValidLocation, ObjectSearchState, } from '../../store/objectSearch/ObjectSearchState';
import { bindActionCreators } from 'redux';
import { exposeTestId } from '../../config';
import { selectObjectSearchState } from '../../store/selectors';
import { GraphTokenProvider, renderInlineCssVars, useDesignTokens, } from '@sas/nova-commons/theme';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { useResizeObserverRef } from '@sas/nova-commons/hooks/useResizeObserver';
import ReactFocusLock from 'react-focus-lock';
import { useI18n } from '../../locale/I18nContext';
import { getFirstFocusable } from '@sas/nova-commons/util/focus';
import useOnFocusOut from '@sas/nova-commons/hooks/useOnFocusOut';
import { DiscardChangesDialog } from './panels/components/DiscardChangesDialog';
import { FeatureErrorDialog } from './panels/components/FeatureErrorDialog';
import { PopoverManagerContext, usePopoverManager, } from '@sas/nova-commons/components/Popover/PopoverManager';
import { useShowMessageToast } from '@sas/nova-commons/components/MessageToast';
import cx from 'classnames';
import cn from './ObjectSearch.css.json';
import './ObjectSearch.css';
export const ObjectSearchBase = (props) => {
    var _a, _b;
    const [forceOpen, setForceOpen] = useState(false);
    const [forceClose, setForceClose] = useState(false);
    const dt = useDesignTokens();
    const localize = useI18n();
    const refPanelHasFocus = useRef(false);
    const [panelHasFocus, _setPanelHasFocus] = useState(false); // true if the panel currently has focus
    const [hasHadFocus, setHasHadFocus] = useState(false); // true if ObjectSearch has ever received focus
    const setPanelHasFocus = useCallback((hasFocus) => {
        refPanelHasFocus.current = hasFocus;
        setHasHadFocus((hasHadFocus) => hasHadFocus || hasFocus);
        _setPanelHasFocus(hasFocus);
    }, []);
    const hasNavSelection = !!((_a = props.navSelection) === null || _a === void 0 ? void 0 : _a.length);
    const isOpen = props.pinned ||
        ((forceOpen || hasNavSelection || panelHasFocus || props.pinModeEnabled) && !forceClose);
    // Delete empty features
    {
        const showMessageToast = useShowMessageToast();
        const [emptyFeatureIndex, emptyFeatureType] = (() => {
            if (!props.features || hasNavSelection) {
                return [];
            }
            const featureIndex = props.features.findIndex((feature) => {
                const items = isGeographicRoute(feature) ? feature.routeLocations : [feature];
                const hasValidLocation = items.some((item) => {
                    return typeof item === 'number' ? true : isValidLocation(item.latLon);
                });
                return !hasValidLocation;
            });
            if (featureIndex === -1) {
                return [];
            }
            const featureType = props.features[featureIndex].type;
            return [featureIndex, featureType];
        })();
        useEffect(() => {
            if (typeof emptyFeatureIndex === 'number') {
                props.actions.notifyDeletePins(props.id, [emptyFeatureIndex]);
                showMessageToast(localize({
                    key: emptyFeatureType === 'route'
                        ? 'RoutePanel.invalid.location.txt'
                        : emptyFeatureType === 'area'
                            ? 'GeographicAreaPanel.invalid.location.txt'
                            : 'PinPanel.invalid.location.txt',
                }), {
                    closeable: true,
                });
            }
        }, [emptyFeatureIndex, emptyFeatureType, showMessageToast, localize, props.actions, props.id]);
    }
    useEffect(() => {
        if (isOpen) {
            props.actions.notifyOpen(props.id);
        }
    }, [props.id, props.actions, isOpen]);
    const [setParentRef, getParentRef] = useResizeObserverRef((entry) => {
        const { height, width } = entry.target.getBoundingClientRect();
        if ((parentSize === null || parentSize === void 0 ? void 0 : parentSize.height) !== height || (parentSize === null || parentSize === void 0 ? void 0 : parentSize.width) !== width) {
            setParentSize({ height, width });
        }
    });
    const { onBlur, onFocus } = useOnFocusOut(() => {
        setPanelHasFocus(false);
    });
    {
        const lostFocus = !panelHasFocus && !props.pinned;
        useEffect(() => {
            var _a, _b;
            if (!lostFocus || refPanelHasFocus.current) {
                return;
            }
            if ((_b = (_a = getParentRef()) === null || _a === void 0 ? void 0 : _a.firstElementChild) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement)) {
                // Portable will trigger an update to navigation
                return;
            }
            props.actions.update(props.id, { navSelection: [] });
        }, [lostFocus, getParentRef, props.actions, props.id]);
    }
    useEffect(() => {
        if (forceOpen) {
            setForceOpen(false);
        }
    }, [forceOpen]);
    useEffect(() => {
        if (forceClose) {
            setPanelHasFocus(false);
            setForceClose(false);
        }
    }, [setPanelHasFocus, forceClose]);
    const buttonTokens = renderInlineCssVars(getButtonTokens(dt), 'button');
    const refContainer = useRef(null);
    // Autofocus the button when the user collapses the panel
    const refOpenButton = useRef(null);
    const refAutofocus = useRef(false);
    useEffect(() => {
        var _a, _b, _c;
        if (refAutofocus.current) {
            if (isOpen) {
                (_b = getFirstFocusable((_a = refContainer.current) !== null && _a !== void 0 ? _a : undefined)) === null || _b === void 0 ? void 0 : _b.focus();
            }
            else {
                (_c = refOpenButton.current) === null || _c === void 0 ? void 0 : _c.focus();
            }
        }
        refAutofocus.current = false;
    }, [isOpen]);
    const [parentSize, setParentSize] = useState(undefined);
    // Hack: spy on popoverManager so we know which popovers were opened by ObjectSearch.
    let popoverManagerSpy;
    let refPopoverRoots;
    {
        refPopoverRoots = useRef(undefined);
        if (!refPopoverRoots.current) {
            refPopoverRoots.current = new Map();
        }
        const popoverManager = usePopoverManager();
        // This isn't memo'd, but neither is the original
        popoverManagerSpy = Object.assign(Object.assign({}, popoverManager), { openPopover(referenceDetails) {
                const id = popoverManager.openPopover(referenceDetails);
                refPopoverRoots.current.set(id, referenceDetails.popoverElement);
                return id;
            },
            closePopover(id) {
                refPopoverRoots.current.delete(id);
                return popoverManager.closePopover(id);
            } });
    }
    if (!isOpen) {
        return (React.createElement("div", { className: cx(cn.buttonContainer, cn.positionTopLeft, !props.visible && cn.hidden, props.className), style: Object.assign(Object.assign({}, buttonTokens), props.style), ref: (e) => {
                var _a;
                setParentRef((_a = e === null || e === void 0 ? void 0 : e.parentElement) !== null && _a !== void 0 ? _a : null);
            }, onContextMenu: (event) => {
                event.preventDefault();
            }, onFocus: () => setHasHadFocus(true) },
            React.createElement(Button, { ref: refOpenButton, className: cn.button, style: buttonTokens, variant: ButtonVariant.Icon, icon: { icon: Search, tooltip: localize({ key: 'ObjectSearch.title.txt' }) }, onPress: () => {
                    refAutofocus.current = true;
                    setForceOpen(true);
                }, tokens: () => {
                    return { bd: dt.c.n_1 };
                } })));
    }
    let panelKey;
    let panelContent;
    if (((_b = props.navSelection) === null || _b === void 0 ? void 0 : _b.length) === 1) {
        const itemPosition = props.navSelection[0];
        panelKey = itemPosition.type;
        switch (itemPosition.type) {
            case 'result':
                panelContent = React.createElement(SearchResultPanel, { id: props.id, searchIndex: itemPosition.index });
                break;
            case 'pin':
            case 'myLocationPin':
                panelContent = React.createElement(PinPanel, { id: props.id, featureIndex: itemPosition.index });
                break;
            case 'area':
                panelContent = React.createElement(GeographicAreaPanel, { id: props.id, featureIndex: itemPosition.index });
                break;
            case 'route':
                panelContent = React.createElement(RoutePanel, { id: props.id, featureIndex: itemPosition.index });
                break;
        }
    }
    panelKey = panelKey !== null && panelKey !== void 0 ? panelKey : 'main';
    panelContent = panelContent !== null && panelContent !== void 0 ? panelContent : (React.createElement(MainSearchPanel, { id: props.id, onCollapse: () => {
            if (props.pinned) {
                // if we force a collapse - also unpin if required
                props.actions.setPinned(props.id, false);
            }
            refAutofocus.current = true;
            setForceClose(true);
        } }));
    return (React.createElement(PopoverManagerContext.Provider, { value: popoverManagerSpy },
        React.createElement("div", { className: cx(cn.container, cn.positionTopLeft, props.className), style: props.style, onBlur: onBlur, onFocus: () => {
                setPanelHasFocus(true);
                onFocus();
            }, onKeyDown: (e) => {
                if (e.key === 'Escape') {
                    refAutofocus.current = true;
                    setForceClose(true);
                    props.actions.update(props.id, { navSelection: [] });
                }
            }, ref: (e) => {
                var _a;
                setParentRef((_a = e === null || e === void 0 ? void 0 : e.parentElement) !== null && _a !== void 0 ? _a : null);
                setRef(refContainer, e);
            }, onContextMenu: (event) => {
                event.preventDefault();
                event.stopPropagation();
            }, role: "dialog", "aria-label": localize({ key: 'ObjectSearch.title.txt' }) },
            React.createElement(ReactFocusLock, { disabled: !hasHadFocus, lockProps: {
                    style: { display: 'flex' },
                }, whiteList: (element) => {
                    // We're only using FocusLock for tab order, so only steal focus if the element is in the panel, MessageToast root, or one of our popovers
                    const validRoots = [
                        refContainer.current,
                        document.getElementById('static-area'),
                        ...refPopoverRoots.current.values(),
                    ];
                    return validRoots.some((root) => root === null || root === void 0 ? void 0 : root.contains(element));
                } },
                React.createElement(Panel, { autofocus: hasHadFocus, className: cn.enablePointer, maxHeight: parentSize === null || parentSize === void 0 ? void 0 : parentSize.height, maxWidth: parentSize === null || parentSize === void 0 ? void 0 : parentSize.width, 
                    // Visualization min size
                    minHeight: 100, minWidth: 100, size: props.panelSize, onSizeChange: (panelSize) => props.actions.update(props.id, { panelSize }), contentKey: panelKey },
                    React.createElement(ErrorBoundary, { errorMessageKey: { key: 'ReportContainer.component.renderError.txt' }, style: {
                            position: 'static',
                            height: '100%',
                            width: '100%',
                            padding: '1rem',
                            boxSizing: 'border-box',
                        } },
                        React.createElement(GraphTokenProvider, null, panelContent))),
                React.createElement("div", { className: cx(cn.pinContainer, cn.enablePointer), style: buttonTokens },
                    React.createElement(ToggleButton, { className: cn.button, icon: {
                            icon: props.pinned ? PinPropertyPane : UnpinnedPropertyPane,
                            tooltip: localize({
                                key: props.pinned ? 'ObjectSearch.unpin.txt' : 'ObjectSearch.pin.txt',
                            }),
                        }, pressed: props.pinned, onPress: () => props.actions.setPinned(props.id, !props.pinned), testId: exposeTestId() ? 'object-search-pin' : null, tokens: () => {
                            // Unselected ToggleButton does not have a border by default
                            return { bd: dt.c.n_1 };
                        } }))),
            React.createElement(DiscardChangesDialog, { id: props.id, onResetFocus: () => {
                    setPanelHasFocus(false);
                } }),
            React.createElement(FeatureErrorDialog, { id: props.id }))));
};
export const ObjectSearch = connect((state, props) => {
    var _a;
    const { pinned, navSelection, features, panelSize, pinModeEnabled, minPanelHeight } = (_a = selectObjectSearchState(state, props.id)) !== null && _a !== void 0 ? _a : {};
    return { pinned, navSelection, features, panelSize, pinModeEnabled, minPanelHeight };
}, (dispatch) => ({
    actions: bindActionCreators(ObjectSearchState.actions, dispatch),
}))(ObjectSearchBase);
function getButtonTokens(dt) {
    return {
        'pin-bg': dt.reportTokens.backgroundsDepth.standard.bg,
        bg: dt.reportTokens.backgrounds.page.bg,
        bd: dt.reportTokens.stateStandard.normal.bd,
        'hover-bd': dt.reportTokens.stateStandard.normal.bd,
    };
}
